import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Building2, Users, MessageSquare, Mail, TrendingUp, DollarSign,
  ArrowUpRight, ArrowDownRight, Loader2,
} from "lucide-react";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const fmt = (n: number) => n.toLocaleString("pt-BR");
const fmtBRL = (n: number) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

export function OverviewTab() {
  const { data, isLoading } = trpc.superAdmin.platformOverview.useQuery();

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando visão da plataforma…
      </div>
    );
  }

  const { kpis, topTenantsByUsage, growthSeries } = data;

  // Variação simples M-1 → M (último vs penúltimo ponto da série).
  const series = growthSeries ?? [];
  const last = series.at(-1);
  const prev = series.at(-2);
  const tenantsDelta = last && prev ? last.tenants - prev.tenants : 0;
  const mrrDelta = last && prev ? last.mrr - prev.mrr : 0;

  const KPI = ({
    label, value, icon: Icon, hint, delta, accent,
  }: {
    label: string; value: string; icon: any; hint?: string;
    delta?: number; accent?: "teal" | "accent" | "violet" | "amber";
  }) => {
    const accentClass =
      accent === "violet" ? "bg-violet-500/10 text-violet-600 dark:text-violet-400"
      : accent === "amber" ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
      : accent === "accent" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : "bg-primary/10 text-primary";
    return (
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
              <p className="text-2xl font-bold mt-1.5 truncate">{value}</p>
              {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
              {delta !== undefined && delta !== 0 && (
                <div className={`flex items-center gap-1 mt-1 text-[11px] font-medium ${delta > 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {delta > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {Math.abs(delta)} vs mês anterior
                </div>
              )}
            </div>
            <div className={`p-2 rounded-lg ${accentClass} shrink-0`}>
              <Icon className="w-4 h-4" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">Visão Geral</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Cockpit de saúde do negócio — MRR, base ativa, consumo do mês.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="MRR estimado" value={fmtBRL(kpis.mrrEstimate)} icon={DollarSign} accent="accent" delta={mrrDelta} hint={`ARR ~ ${fmtBRL(kpis.arrEstimate)}`} />
        <KPI label="Empresas Ativas" value={`${kpis.activeTenants}/${kpis.totalTenants}`} icon={Building2} delta={tenantsDelta} hint={`${kpis.suspended} suspensa(s) · ${kpis.cancelled} cancelada(s)`} />
        <KPI label="Novos no mês" value={fmt(kpis.newThisMonth)} icon={TrendingUp} accent="violet" />
        <KPI label="Contatos (rede toda)" value={fmt(kpis.contactsTotal)} icon={Users} />
        <KPI label="Mensagens WhatsApp / mês" value={fmt(kpis.msgsThisMonth)} icon={MessageSquare} accent="violet" />
        <KPI label="E-mails enviados / mês" value={fmt(kpis.emailsThisMonth)} icon={Mail} accent="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Crescimento (últimos 12 meses)</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="left"  type="monotone" dataKey="tenants" name="Empresas"   stroke="#0d9488" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="mrr"     name="MRR (R$)"   stroke="#22c55e" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top consumidores</CardTitle>
          </CardHeader>
          <CardContent>
            {topTenantsByUsage.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Sem dados ainda.</p>
            ) : (
              <div className="space-y-3">
                {topTenantsByUsage.map((t, idx) => (
                  <div key={t.id} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{t.name}</p>
                        <Badge variant={t.status === "active" ? "default" : "secondary"} className="text-[9px] h-4 px-1.5">
                          {t.status}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        {fmt(t.msgs)} msgs · {fmt(t.contacts)} contatos
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
